// # Expense Overview Calculator
// # This file contains utility functions for expense calculations

/**
 * # Calculate total expenses from an array of expense objects
 * @param {Array} expenses - Array of expense objects
 * @returns {Number} - Total expenses in EGP
 */
export const calculateTotalExpenses = (expenses) => {
  return expenses.reduce((sum, expense) => sum + Number(expense.egp_total || 0), 0);
};

/**
 * # Calculate average transaction amount
 * @param {Array} expenses - Array of expense objects
 * @returns {Number} - Average transaction amount in EGP
 */
export const calculateAverageTransaction = (expenses) => {
  const total = calculateTotalExpenses(expenses);
  return expenses.length ? total / expenses.length : 0;
};

/**
 * # Group expenses by month
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object with month keys and expense totals
 */
export const groupExpensesByMonth = (expenses) => {
  const expensesByMonth = {};
  expenses.forEach(expense => {
    const date = new Date(expense.timestamp || expense.metadata?.created_at);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    expensesByMonth[monthKey] = (expensesByMonth[monthKey] || 0) + Number(expense.egp_total || 0);
  });
  return expensesByMonth;
};

/**
 * # Group expenses by year
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object with year keys and expense totals
 */
export const groupExpensesByYear = (expenses) => {
  const expensesByYear = {};
  expenses.forEach(expense => {
    const date = new Date(expense.timestamp || expense.metadata?.created_at);
    const year = date.getFullYear().toString();
    expensesByYear[year] = (expensesByYear[year] || 0) + Number(expense.egp_total || 0);
  });
  return expensesByYear;
};

/**
 * # Group expenses by month for a specific year
 * @param {Array} expenses - Array of expense objects
 * @param {Number} year - Year to filter by
 * @returns {Object} - Object with month keys and expense totals
 */
export const groupExpensesByMonthForYear = (expenses, year) => {
  const expensesByMonth = {};
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // # Initialize all months with zero
  monthNames.forEach((name, index) => {
    expensesByMonth[name] = 0;
  });
  
  // # Fill in actual expense data
  expenses.forEach(expense => {
    const date = new Date(expense.timestamp || expense.metadata?.created_at);
    if (date.getFullYear() === Number(year)) {
      const monthName = monthNames[date.getMonth()];
      expensesByMonth[monthName] = (expensesByMonth[monthName] || 0) + Number(expense.egp_total || 0);
    }
  });
  
  return expensesByMonth;
};

/**
 * # Group expenses by day for a specific month and year
 * @param {Array} expenses - Array of expense objects
 * @param {Number} year - Year to filter by
 * @param {Number} month - Month to filter by (1-12)
 * @returns {Object} - Object with day keys and expense totals
 */
export const groupExpensesByDayForMonth = (expenses, year, month) => {
  const expensesByDay = {};
  const daysInMonth = new Date(year, month, 0).getDate();
  
  // # Initialize all days with zero
  for (let i = 1; i <= daysInMonth; i++) {
    expensesByDay[i] = 0;
  }
  
  // # Fill in actual expense data
  expenses.forEach(expense => {
    const date = new Date(expense.timestamp || expense.metadata?.created_at);
    if (date.getFullYear() === Number(year) && date.getMonth() + 1 === Number(month)) {
      const day = date.getDate();
      expensesByDay[day] = (expensesByDay[day] || 0) + Number(expense.egp_total || 0);
    }
  });
  
  return expensesByDay;
};

/**
 * # Group expenses by category
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object with category keys and expense totals
 */
export const groupExpensesByCategory = (expenses) => {
  const expensesByCategory = {};
  expenses.forEach(expense => {
    if (expense.category) {
      expensesByCategory[expense.category] = (expensesByCategory[expense.category] || 0) + Number(expense.egp_total || 0);
    }
  });
  return expensesByCategory;
};

/**
 * # Count expenses by currency
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object with currency keys and counts
 */
export const countExpensesByCurrency = (expenses) => {
  const currencyCount = {};
  expenses.forEach(expense => {
    if (expense.currency) {
      currencyCount[expense.currency] = (currencyCount[expense.currency] || 0) + 1;
    }
  });
  return currencyCount;
};

/**
 * # Find the most used currency
 * @param {Array} expenses - Array of expense objects
 * @returns {String} - Most used currency code
 */
export const findMostUsedCurrency = (expenses) => {
  const currencyCount = countExpensesByCurrency(expenses);
  return Object.entries(currencyCount)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || '';
};

/**
 * # Find the top spending category
 * @param {Array} expenses - Array of expense objects
 * @returns {String} - Top spending category name
 */
export const findTopCategory = (expenses) => {
  const expensesByCategory = groupExpensesByCategory(expenses);
  return Object.entries(expensesByCategory)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || '';
};

/**
 * # Calculate monthly growth rate
 * @param {Object} expensesByMonth - Object with month keys and expense totals
 * @returns {Number} - Monthly growth rate as a percentage
 */
export const calculateMonthlyGrowthRate = (expensesByMonth) => {
  const monthsArray = Object.entries(expensesByMonth)
    .sort((a, b) => a[0].localeCompare(b[0]));
  
  if (monthsArray.length < 2 || monthsArray[0][1] === 0) {
    return 0;
  }
  
  return ((monthsArray[monthsArray.length - 1][1] - monthsArray[0][1]) / monthsArray[0][1]) * 100;
};

/**
 * # Calculate yearly growth rate
 * @param {Object} expensesByYear - Object with year keys and expense totals
 * @returns {Number} - Yearly growth rate as a percentage
 */
export const calculateYearlyGrowthRate = (expensesByYear) => {
  const yearsArray = Object.entries(expensesByYear)
    .sort((a, b) => a[0].localeCompare(b[0]));
  
  if (yearsArray.length < 2 || yearsArray[0][1] === 0) {
    return 0;
  }
  
  return ((yearsArray[yearsArray.length - 1][1] - yearsArray[0][1]) / yearsArray[0][1]) * 100;
};

/**
 * # Calculate yearly projection based on monthly average
 * @param {Object} expensesByMonth - Object with month keys and expense totals
 * @returns {Number} - Yearly projection in EGP
 */
export const calculateYearlyProjection = (expensesByMonth) => {
  const totalExpenses = Object.values(expensesByMonth).reduce((sum, amount) => sum + amount, 0);
  const monthCount = Object.keys(expensesByMonth).length || 1;
  const monthlyAverage = totalExpenses / monthCount;
  return monthlyAverage * 12;
};

/**
 * # Calculate monthly average for a year
 * @param {Object} expensesByMonthForYear - Object with month names and expense totals
 * @returns {Number} - Monthly average for the year
 */
export const calculateMonthlyAverageForYear = (expensesByMonthForYear) => {
  const totalExpenses = Object.values(expensesByMonthForYear).reduce((sum, amount) => sum + amount, 0);
  const monthCount = Object.values(expensesByMonthForYear).filter(amount => amount > 0).length || 1;
  return totalExpenses / monthCount;
};

/**
 * # Filter expenses by date range
 * @param {Array} expenses - Array of expense objects
 * @param {String} startDate - Start date in YYYY-MM-DD format
 * @param {String} endDate - End date in YYYY-MM-DD format
 * @returns {Array} - Filtered expenses
 */
export const filterExpensesByDateRange = (expenses, startDate, endDate) => {
  return expenses.filter(expense => {
    const expenseDate = (expense.timestamp || expense.metadata?.created_at).split('T')[0];
    return expenseDate >= startDate && expenseDate <= endDate;
  });
};

/**
 * # Calculate comprehensive expense statistics
 * @param {Array} expenses - Array of expense objects
 * @returns {Object} - Object containing various expense statistics
 */
export const calculateExpenseStats = (expenses) => {
  const expensesByMonth = groupExpensesByMonth(expenses);
  const expensesByYear = groupExpensesByYear(expenses);
  const expensesByCategory = groupExpensesByCategory(expenses);
  const currencyCount = countExpensesByCurrency(expenses);
  
  // # Daily expenses
  const dailyExpenses = {};
  expenses.forEach(expense => {
    const date = (expense.timestamp || expense.metadata?.created_at).split('T')[0];
    dailyExpenses[date] = (dailyExpenses[date] || 0) + Number(expense.egp_total || 0);
  });
  
  // # Calculate current month expenses
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthExpenses = expensesByMonth[currentMonth] || 0;
  
  // # Calculate current year expenses
  const currentYear = currentDate.getFullYear().toString();
  const currentYearExpenses = expensesByYear[currentYear] || 0;
  
  // # Calculate monthly expenses for current year
  const currentYearMonthlyExpenses = groupExpensesByMonthForYear(expenses, currentYear);
  
  // # Calculate other stats
  const totalExpenses = calculateTotalExpenses(expenses);
  const averageTransaction = calculateAverageTransaction(expenses);
  const mostUsedCurrency = findMostUsedCurrency(expenses);
  const topCategory = findTopCategory(expenses);
  const highestExpense = Math.max(...Object.values(expensesByCategory), 0);
  const monthlyAverage = totalExpenses / (Object.keys(expensesByMonth).length || 1);
  const sortedMonths = Object.entries(expensesByMonth).sort((a, b) => b[1] - a[1]);
  const highestMonth = sortedMonths[0]?.[0] || '';
  const dailyAverage = totalExpenses / (Object.keys(dailyExpenses).length || 1);
  const monthlyGrowth = calculateMonthlyGrowthRate(expensesByMonth);
  const yearlyGrowth = calculateYearlyGrowthRate(expensesByYear);
  const yearlyProjection = calculateYearlyProjection(expensesByMonth);
  
  return {
    totalExpenses,
    currentMonthExpenses,
    currentYearExpenses,
    averageTransaction,
    mostUsedCurrency,
    topCategory,
    categoryCount: Object.keys(expensesByCategory).length,
    highestExpense,
    monthlyAverage,
    highestMonth,
    dailyAverage,
    monthlyGrowth,
    yearlyGrowth,
    yearlyProjection,
    expensesByMonth,
    expensesByYear,
    currentYearMonthlyExpenses,
    expensesByCategory,
    currencyCount,
    dailyExpenses
  };
};
